import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { withLoadResilience } from './loadResilience';
import type { RootState } from '@/app/store';

/**
 * Endpoints that must never receive the consumer Token header.
 * These are AllowAny / guest-Bearer endpoints; injecting the consumer Token
 * onto them causes 401s (getTableSession) or leaks identity (resolve/join).
 * Belt-and-braces: also checked via headers.has('Authorization') below.
 */
const TABLE_SESSION_ENDPOINTS = new Set([
  'resolveTable',
  'joinTableSession',
  'getTableSession',
  // SC-169 shared table cart — guest-Bearer auth, never the consumer Token.
  'addSharedCartLine',
  'updateSharedCartLineQty',
  'removeSharedCartLine',
  'submitSharedCart',
]);

export const saleApi = createApi({
  reducerPath: 'saleApi',
  baseQuery: withLoadResilience(
    fetchBaseQuery({
      baseUrl: import.meta.env.VITE_SALE_API_URL ?? 'http://localhost:8003',
      credentials: 'include',
      prepareHeaders: (headers, { getState, endpoint }) => {
        const token = (getState() as RootState).user?.token;
        // Skip the consumer Token when:
        //   (a) the per-request headers already carry an Authorization value, OR
        //   (b) the endpoint is in the tableSession guest-auth allowlist.
        // Belt-and-braces: both guards applied.
        if (token && !headers.has('Authorization') && !TABLE_SESSION_ENDPOINTS.has(endpoint)) {
          headers.set('Authorization', `Token ${token}`);
        }
        return headers;
      },
    }),
  ),
  // WaiterTables/Takeaway/SessionDetail — spec §1.3 G4: WaiterPendingOrdersHost
  // holds the single waiter SSE stream and invalidates these on the relevant
  // events so every subscribed waiter-mode query (tables/takeaway lists, the
  // open ticket's session detail) refetches without opening a second stream.
  // WaiterAreas/OnlineOrders — 2026-08-28 waiter-salle-ux spec §4: salles CRUD
  // and the online-orders list/detail. OnlineOrders is invalidated by the
  // same SSE host (`order_updated` etc. also touch online orders).
  tagTypes: ['Order', 'TableSession', 'WaiterTables', 'Takeaway', 'SessionDetail', 'WaiterAreas', 'OnlineOrders', 'WaiterReservations'],
  endpoints: () => ({}),
});
